import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { Phrase } from '../../model/phrase';
import { Translation } from '../../model/translation';
import { LanguageService } from '../../service/language.service';
import { FormControlEx, toErrorMessage } from '../forms/form-control';
import { FormGroupValidators } from '../forms/form-group.validators';

@Component({
    selector: 'translate-dialog-form',
    templateUrl: './translate-dialog-form.component.html',
    styleUrls: ['./translate-dialog-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TranslateDialogFormComponent implements OnInit {
  constructor(
    private _dialogService: ActionWindowService,
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _languageService: LanguageService
  ) {}

  @Input() dataContext: {
    phrase: Phrase;
    validator: ValidatorFn;
    cultures: string[];
    type: 'box' | 'area';
    isReadonly: boolean;
    requiredCulture?: string;
    atLeastOne: boolean; // default true
    cannotRemove: boolean; // default true
  };

  form: FormGroup;
  translations: FormControlEx[] = [];

  cancel() {
    this._dialogService.close();
  }

  ngOnInit() {
    const controls: { [key: string]: FormControlEx } = {};
    this.translations = this.dataContext.phrase
      .getAllTranslations()
      .filter(t => (this.dataContext.cultures?.length ?? 0) === 0 || this.dataContext.cultures.includes(t.cultureName))
      .map(t => {
        const caption = this._languageService.getCultureLanguageName(t.cultureName);
        const isRequired = t.cultureName === this.dataContext.requiredCulture;
        const validators = [this.dataContext.validator, isRequired ? Validators.required : null].filter(Boolean) as ValidatorFn[];
        const control = this._fc.createWithOptions(caption, t.translationText, {
          isReadonly: this.dataContext.isReadonly,
          validator: validators.length ? Validators.compose(validators) : null,
          required: isRequired,
        });
        const cannotRemove = this.dataContext.cannotRemove !== false;
        if (cannotRemove) {
          control.addValidators(Translation.cannotRemove(t));
        }
        controls[t.cultureName] = control;
        return control;
      });

    this.form = this._fb.group(controls, {
      validators:
        this.dataContext.atLeastOne !== false
          ? FormGroupValidators.atLeastOne({
              controlNameTranslator: name => this._languageService.getCultureLanguageName(name),
            })
          : null,
    });
  }

  get errorMessage(): { message: string; params: any } {
    return this.form.pristine || this.form.valid ? null : toErrorMessage(this.form.errors);
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.markAsDirty();
      return;
    }
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.controls[key];
      if (control) {
        this.dataContext.phrase.setTranslation(key, control.value);
      }
    });
    this._dialogService.complete(this.dataContext.phrase);
  }
}
